import { useEffect, useState } from "react";
import { api, imgUrl } from "../lib/api";
import { X } from "lucide-react";

const TYPES = ["all", "fabric", "print", "colorway"];
const SOURCES = [
  { key: "all", label: "All" },
  { key: "material", label: "Ad-Hoc" },
  { key: "plm", label: "Libraries" },
];

export function MaterialPicker({ value, onChange, testIdPrefix = "material-picker" }) {
  const [materials, setMaterials] = useState([]);
  const [filter, setFilter] = useState("all");
  const [source, setSource] = useState("all");

  useEffect(() => {
    api.get("/materials").then((r) => setMaterials(r.data)).catch(() => {});
  }, []);

  const filtered = materials.filter((m) => {
    if (filter !== "all" && m.type !== filter) return false;
    if (source !== "all" && (m.source || "material") !== source) return false;
    return true;
  });
  const selected = materials.find((m) => m.id === value);

  return (
    <div>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <label className="text-xs uppercase tracking-[0.15em] text-zinc-500">Fabric / print / colorway (optional)</label>
        <div className="flex gap-1">
          {TYPES.map((t) => (
            <button key={t} data-testid={`${testIdPrefix}-filter-${t}`} onClick={() => setFilter(t)}
              className={`px-2.5 py-1 rounded text-[11px] capitalize transition-colors ${filter === t ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100"}`}>{t}</button>
          ))}
        </div>
      </div>
      <div className="mt-1.5 flex items-center gap-1">
        <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Source</span>
        {SOURCES.map((s) => (
          <button key={s.key} data-testid={`${testIdPrefix}-source-${s.key}`} onClick={() => setSource(s.key)}
            className={`px-2 py-0.5 rounded text-[10px] transition-colors ${source === s.key ? "bg-zinc-950 text-white" : "text-zinc-500 hover:bg-zinc-100 border border-zinc-200"}`}>{s.label}</button>
        ))}
      </div>
      {selected && (
        <div className="mt-2 flex items-center gap-2 border border-zinc-200 rounded-md p-2 bg-zinc-50" data-testid={`${testIdPrefix}-selected`}>
          <img src={imgUrl(selected.image_url)} alt={selected.name} className="w-8 h-8 rounded object-cover" />
          <p className="text-xs font-medium flex-1 truncate">{selected.name}
            <span className="text-zinc-400 font-normal capitalize"> · {selected.type}</span>
            {selected.source === "plm" && <span className="ml-1 text-[9px] uppercase tracking-[0.1em] bg-zinc-950 text-white rounded px-1 py-0.5">PLM</span>}
          </p>
          <button data-testid={`${testIdPrefix}-clear`} onClick={() => onChange("")} title="Remove material"
            className="p-1 rounded hover:bg-zinc-200 text-zinc-500 transition-colors"><X size={13} /></button>
        </div>
      )}
      <div className="mt-2 grid grid-cols-4 gap-2 max-h-48 overflow-y-auto pr-1">
        {filtered.length === 0 && source === "plm" && (
          <p className="col-span-4 text-[11px] text-zinc-500 border border-dashed border-zinc-200 rounded-md p-3">
            No PLM Library items match — add a <a href="/libraries" className="underline decoration-dotted hover:text-zinc-950">Fabric or Print with an artwork/swatch</a> in Libraries and it will show up here automatically.
          </p>
        )}
        {filtered.length === 0 && source !== "plm" && (
          <p className="col-span-4 text-[11px] text-zinc-500 border border-dashed border-zinc-200 rounded-md p-3">
            No Ad-Hoc materials — upload one under <a href="/library" className="underline decoration-dotted hover:text-zinc-950">Ad-Hoc Materials</a> or switch to Libraries to pick a PLM item.
          </p>
        )}
        {filtered.map((m) => (
          <button key={m.id} data-testid={`${testIdPrefix}-swatch-${m.id}`} onClick={() => onChange(value === m.id ? "" : m.id)} title={`${m.name}${m.source === "plm" ? " · PLM Library" : " · Ad-Hoc"}`}
            className={`relative border rounded-md overflow-hidden transition-colors ${value === m.id ? "border-zinc-950 ring-2 ring-zinc-950" : "border-zinc-200 hover:border-zinc-400"}`}>
            {m.source === "plm" && (
              <span className="absolute top-1 left-1 bg-zinc-950 text-white text-[8px] uppercase tracking-[0.1em] font-medium px-1 py-0.5 rounded z-10">PLM</span>
            )}
            <img src={imgUrl(m.image_url)} alt={m.name} className="w-full h-14 object-cover" />
            <p className="text-[10px] p-1 truncate">{m.name}</p>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-zinc-400 mt-1.5">
        Optional — pick from your Ad-Hoc uploads or PLM Libraries (fabrics & prints with an artwork appear here automatically with a PLM badge).
      </p>
    </div>
  );
}
